import React, { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import { Search } from "@mui/icons-material";
import { useUnit } from "effector-react";
import { getFraudBlocksEffect } from "../../stores/FraudBlocksStore/FraudBlocksStore.effects";
import { FraudBlocksStore } from "../../stores/FraudBlocksStore/FraudBlocksStore";
import { FRAUD_BLOCKS_PAGE_SIZE } from "../../stores/FraudBlocksStore/FraudBlocksStore.consts";
import BlockedUserRow from "./components/BlockedUserRow/BlockedUserRow";
import { NotFound } from "./BlockedUsersStyles";
import { PageContainer, PageHeader, PageTitle } from "../../components/PageLayout";

const SEARCH_DEBOUNCE_MS = 500;

const BlockedUsers: React.FC = () => {
  const { blocks, totalElements } = useUnit(FraudBlocksStore);
  const loading = useUnit(getFraudBlocksEffect.pending);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(FRAUD_BLOCKS_PAGE_SIZE);

  /** Aguarda o usuário parar de digitar antes de consultar a API */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    getFraudBlocksEffect({ page, size: rowsPerPage, search: debouncedSearch });
  }, [page, rowsPerPage, debouncedSearch]);

  const renderBlocks = () => {
    if (loading)
      return (
        <tr>
          <NotFound colSpan={6}>
            <CircularProgress size={28} />
          </NotFound>
        </tr>
      );

    if (!blocks?.length)
      return (
        <tr>
          <NotFound colSpan={6}>
            {debouncedSearch
              ? "Nenhum resultado encontrado"
              : "Sem usuários bloqueados para exibir"}
          </NotFound>
        </tr>
      );

    return blocks.map((block) => <BlockedUserRow key={block.id} block={block} />);
  };

  return (
    <PageContainer style={{ height: "100%", boxSizing: "border-box", overflow: "hidden" }}>
      <PageHeader>
        <PageTitle>Usuários bloqueados</PageTitle>
        <TextField
          size="small"
          placeholder="Buscar por CPF, nome, e-mail, motivo..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            width: { xs: "100%", sm: 340 },
            "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </PageHeader>
      <Paper
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <Table stickyHeader sx={{ minWidth: 650 }} aria-label="blocked users table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: "nowrap" }}>CPF</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Nome</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>E-mail</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Motivo</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Data</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }} align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{renderBlocks()}</TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 50, 100]}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          sx={{ borderTop: 1, borderColor: "divider", flexShrink: 0 }}
        />
      </Paper>
    </PageContainer>
  );
};

export default BlockedUsers;
